import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MapStyle } from '../../shared/models';
import { MapStateService } from '../map-state.service';
import { MapboxService } from '../mapbox-map/mapbox-map.service';

import { filter, Subject, takeUntil } from 'rxjs';
import { LoggingService } from '../../shared/services/logging.service';
import { HistoryStore } from '../../core/store';
import { TranslateModule } from '@ngx-translate/core';
import { MapLayersControlsComponent } from '../map-layers-controls/map-layers-controls.component';


@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'places-map-style-controls',
    templateUrl: './map-style-controls.component.html',
    styleUrls: ['./map-style-controls.component.scss'],
    imports: [MapLayersControlsComponent, TranslateModule],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class MapStyleControlsComponent implements OnInit, OnDestroy {
    private readonly _mapboxService = inject(MapboxService);
    private readonly _mapStateService = inject(MapStateService);
    private readonly _router = inject(Router);
    private readonly _historyStore = inject(HistoryStore);
    private readonly element = inject(ElementRef);
    private readonly renderer = inject(Renderer2);
    private readonly _logger = inject(LoggingService);

    private readonly RIGHT_DEFAULT = '1rem';
    private readonly RIGHT_WITH_OUTLET = 'calc(39rem + 1rem)';
    private readonly RIGHT_WITH_TWO_OUTLETS = 'calc(78rem + 1rem)';

    @Input() mapStyles: MapStyle[];
    public currentStyle: MapStyle;
    public showStyles = true;

    private readonly _ngUnsubscribe$ = new Subject<void>();
    // eslint-disable-next-line @typescript-eslint/naming-convention
    private readonly HISTORY_SCRUBBAR_HEIGHT = 80;

    @Input() public set defaultMapStyle(style: MapStyle) {
        this.currentStyle = style;
    }


    constructor() { }

    ngOnInit() {
        this._updateRight();

        this._router.events.pipe(
            filter(e => e instanceof NavigationEnd),
            takeUntil(this._ngUnsubscribe$)
        ).subscribe(() => {
            this._updateRight();
        });

        this._historyStore.getPropertyState('vehicleHistory')
            .pipe(takeUntil(this._ngUnsubscribe$))
            .subscribe(vehicleHistory => {
                if (vehicleHistory) {
                    this.renderer.setStyle(this.element.nativeElement, 'bottom', `${this.HISTORY_SCRUBBAR_HEIGHT + 10}px`);
                } else {
                    this.renderer.setStyle(this.element.nativeElement, 'bottom', `10px`);
                }
            });
    }

    ngOnDestroy() {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
    }

    public changeMapStyle(style: MapStyle) {
        if (this.currentStyle && style.name === this.currentStyle.name) {
            return;
        }

        this._logger.logEvent('User changed map style changed', { from: this.currentStyle.name, to: style.name });

        this.defaultMapStyle = style;
        this._mapStateService.setStyle(this.defaultMapStyle);
        this._mapboxService.changeStyle(style);
    }

    public toggleButton() {
        this.showStyles = !this.showStyles;
    }

    private _updateRight(): void {
        const hasOutletRight0 = this._router.url.includes('outlet-right0:');
        const hasOutletRight1 = this._router.url.includes('outlet-right1:');
        const hasOutletRight2 = this._router.url.includes('outlet-right2:');
        const hasOutlet2 = this._router.url.includes('outlet2:');

        let rightOutletCount = (hasOutletRight0 ? 1 : 0) + (hasOutletRight1 ? 1 : 0) + (hasOutletRight2 ? 1 : 0) + (hasOutlet2 ? 1 : 0);

        let right = this.RIGHT_DEFAULT;
        if (rightOutletCount >= 2) {
            right = this.RIGHT_WITH_TWO_OUTLETS;
        } else if (rightOutletCount === 1) {
            right = this.RIGHT_WITH_OUTLET;
        }

        this.renderer.setStyle(this.element.nativeElement, 'right', right);
    }
}